import { deriveFormGroupsFromFields, getFieldGroupTitle } from '@common/utils/formGroups';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from '@mui/material';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { updateTemplateFormProperties } from '@/services/TemplateFormsService';
import { errorSnackbar, successSnackbar } from '@/utils/snackbar';
import DeleteGroupDialog from './DeleteGroupDialog';
import { buildIntakeFormPropertiesPayload } from './intakeFormPayload';
import type { IntakeDesignData } from './types';

const SECTION_DESCRIPTION_MAX_LENGTH = 2000;

const multilineTextFieldSx = {
    width: '100%',
    fontSize: '14px',
    '& .MuiOutlinedInput-root': {
        padding: '8px 12px',
        alignItems: 'flex-start',
    },
};

interface Props {
    currentGroupTitle: string;
    quoteTemplateId: number | null;
    intakeDesign: IntakeDesignData;
    setIntakeDesign: Dispatch<SetStateAction<IntakeDesignData>>;
    onClose: () => void;
}

export default function EditGroupDialog({
    currentGroupTitle,
    quoteTemplateId,
    intakeDesign,
    setIntakeDesign,
    onClose,
}: Props) {
    const [isLoading, setIsLoading] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const currentGroup = intakeDesign.formGroups?.find((group) => group.name === currentGroupTitle);
    const [groupTitle, setGroupTitle] = useState(currentGroupTitle);
    const [groupDescription, setGroupDescription] = useState(currentGroup?.description ?? '');

    const onSave = async () => {
        const trimmedGroupTitle = groupTitle.trim();
        if (!trimmedGroupTitle) {
            errorSnackbar('Group name cannot be empty');
            return;
        }
        if (!currentGroupTitle) {
            errorSnackbar('No group selected');
            return;
        }

        const trimmedDescription = groupDescription.trim();
        const nameChanged = trimmedGroupTitle !== currentGroupTitle;
        const descriptionChanged =
            (trimmedDescription || null) !== (currentGroup?.description?.trim() || null);

        if (!nameChanged && !descriptionChanged) {
            onClose();
            return;
        }

        const existingGroups = intakeDesign.formGroups?.length
            ? intakeDesign.formGroups
            : deriveFormGroupsFromFields(intakeDesign.templateFormFields);

        const groupExists = existingGroups.some(
            (group) =>
                group.name.toLowerCase() === trimmedGroupTitle.toLowerCase() &&
                group.name.toLowerCase() !== currentGroupTitle.toLowerCase(),
        );
        if (groupExists) {
            errorSnackbar('A group with this name already exists');
            return;
        }

        let updatedFormGroups = existingGroups.map((group) =>
            group.name === currentGroupTitle
                ? {
                      ...group,
                      name: trimmedGroupTitle,
                      description: trimmedDescription || null,
                  }
                : group,
        );

        if (!updatedFormGroups.some((group) => group.name === trimmedGroupTitle)) {
            const maxDisplayOrder = updatedFormGroups.reduce(
                (max, group) => Math.max(max, group.displayOrder),
                0,
            );
            updatedFormGroups = [
                ...updatedFormGroups,
                {
                    name: trimmedGroupTitle,
                    displayOrder: maxDisplayOrder + 1,
                    description: trimmedDescription || null,
                },
            ];
        }

        const updatedTemplateFormFields = nameChanged
            ? intakeDesign.templateFormFields.map((field) => {
                  // Same precedence as the backend rename, so both move the same questions
                  if (getFieldGroupTitle(field) !== currentGroupTitle) {
                      return field;
                  }

                  return {
                      ...field,
                      formGroupTitle: trimmedGroupTitle,
                      questionSchema: {
                          ...field.questionSchema,
                          formGroupTitle: trimmedGroupTitle,
                      },
                  };
              })
            : intakeDesign.templateFormFields;

        const updatedIntakeDesign = {
            ...intakeDesign,
            formGroups: updatedFormGroups,
            currentIntakeTab: {
                ...intakeDesign.currentIntakeTab,
                tabLabel: trimmedGroupTitle,
            },
            templateFormFields: updatedTemplateFormFields,
        };

        setIntakeDesign(updatedIntakeDesign);

        if (!quoteTemplateId || !intakeDesign.templateFormId) {
            successSnackbar('Group updated successfully');
            onClose();
            return;
        }

        setIsLoading(true);
        try {
            await updateTemplateFormProperties(quoteTemplateId, intakeDesign.templateFormId, {
                // Groups can only be edited on V2 forms
                ...buildIntakeFormPropertiesPayload({
                    intakeDesign,
                    isIntakeFormV2: true,
                    formGroups: updatedFormGroups,
                }),
                // The backend moves the group's questions to the new name
                ...(nameChanged && {
                    renamedGroup: { from: currentGroupTitle, to: trimmedGroupTitle },
                }),
            });
            successSnackbar('Group updated successfully');
            onClose();
        } catch (error: any) {
            setIntakeDesign(intakeDesign);
            console.error(error);
            errorSnackbar(error.response?.data || 'Something went wrong!');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Dialog open onClose={onClose}>
                <DialogTitle>Edit group</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="dense"
                        label="Group name"
                        value={groupTitle}
                        onChange={(event) => setGroupTitle(event.target.value)}
                    />
                    <TextField
                        fullWidth
                        margin="dense"
                        multiline
                        rows={4}
                        label="Section description (optional)"
                        helperText={`Shown at the top of this section for clients. ${groupDescription.length}/${SECTION_DESCRIPTION_MAX_LENGTH}`}
                        value={groupDescription}
                        onChange={(event) => setGroupDescription(event.target.value)}
                        inputProps={{
                            maxLength: SECTION_DESCRIPTION_MAX_LENGTH,
                            style: { resize: 'vertical' },
                        }}
                        sx={multilineTextFieldSx}
                    />
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                    <Button
                        color="error"
                        variant="outlined"
                        sx={{ color: 'error.main' }}
                        onClick={() => setDeleteConfirmOpen(true)}
                        disabled={isLoading}
                    >
                        Delete
                    </Button>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={onClose} disabled={isLoading}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={onSave} disabled={isLoading}>
                            Save
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
            {deleteConfirmOpen && (
                <DeleteGroupDialog
                    currentGroupTitle={currentGroupTitle}
                    quoteTemplateId={quoteTemplateId}
                    intakeDesign={intakeDesign}
                    setIntakeDesign={setIntakeDesign}
                    onClose={() => setDeleteConfirmOpen(false)}
                    onDeleted={onClose}
                />
            )}
        </>
    );
}
